import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JobType } from '../graphql/types';
import * as workflowService from '../services/workflowService';
import * as jobTypeConfigService from '../services/jobTypeConfigService';
import { buildDraftCreatePayload, defaultDraftName, getCreatedItemPath } from './draftModel';

/**
 * useDraftJob: lazy creation of the job behind a DD/BA draft Workspace.
 *
 * A draft is not a stored item. Uploads and loans need a real job id, so the
 * draft creates the job on the first real action (a valid field edit, a file,
 * a tape, loans) with a default name, and then replaces the URL with the new
 * job's Intake (`open`). The response is cached: if a later step fails (saving
 * the loan, adding loans) and the user retries, the same job is used instead of
 * creating a second one.
 *
 * Requirements: 2.3, 3.5.
 */
export interface DraftJobHandle {
  /** The job's response, creating the job on the first call. Throws if creation fails. */
  create: (borrowerName?: string) => Promise<workflowService.CreateJobResponse>;
  /** Replace the URL with the created job's Intake. */
  open: (response: workflowService.CreateJobResponse) => void;
}

export function useDraftJob(feature: JobType.DD | JobType.BA): DraftJobHandle {
  const navigate = useNavigate();
  const created = useRef<workflowService.CreateJobResponse | null>(null);
  const inflight = useRef<Promise<workflowService.CreateJobResponse> | null>(null);

  const create = useCallback(
    (borrowerName = ''): Promise<workflowService.CreateJobResponse> => {
      if (created.current) return Promise.resolve(created.current);
      if (inflight.current) return inflight.current;
      const config = jobTypeConfigService.getConfig(feature);
      const request = workflowService
        .createJob(buildDraftCreatePayload(feature, defaultDraftName(config.displayName), borrowerName))
        .then((response) => {
          created.current = response;
          return response;
        })
        .finally(() => {
          inflight.current = null;
        });
      inflight.current = request;
      return request;
    },
    [feature]
  );

  const open = useCallback(
    (response: workflowService.CreateJobResponse) => {
      navigate(getCreatedItemPath(feature, response.job.jobId), {
        replace: true,
        state: {
          uploadCredentials: response.uploadCredentials,
          uploadBucket: response.uploadBucket,
        },
      });
    },
    [feature, navigate]
  );

  return { create, open };
}
